import type { EntryType, MerkleTreeLeaf, Sct } from '../rfc6962/types';

export type LogKind = 'rfc6962' | 'tiled';
export type LogState = 'usable' | 'qualified' | 'readonly' | 'retired' | 'pending' | 'rejected' | 'unknown';

/** Everything the UI and clients need to know about a log, regardless of where it came from. */
export interface LogDescriptor {
  /** Stable route id: hex SHA-256 of the log key, or `custom-…` for keyless custom logs. */
  id: string;
  /** 32-byte log ID (SHA-256 of the SPKI), when the key is known. */
  logId?: Uint8Array;
  /** DER SubjectPublicKeyInfo of the log's signing key, when known. */
  key?: Uint8Array;
  kind: LogKind;
  description: string;
  operator: string;
  /** RFC 6962 base URL (…/ct/v1/ is appended); equals submissionUrl for tiled logs. */
  url: string;
  submissionUrl: string;
  monitoringUrl: string;
  /** Primary state (Chrome's when listed there, else the first program that lists the log). */
  state: LogState;
  stateTimestamp?: string;
  /** State in every program's list that includes this log. */
  programs: ProgramState[];
  temporalInterval?: { start: string; end: string };
  mmd?: number;
  logType?: string;
  custom?: boolean;
  finalTreeHead?: { treeSize: number; rootHash: string };
}

/** A log's status in one CT program's list. */
export interface ProgramState {
  program: 'chrome' | 'apple' | 'custom';
  state: LogState;
  stateTimestamp?: string;
  /** Program-specific description, when it differs from the primary one. */
  description?: string;
}

export type SignatureStatus = 'verified' | 'failed' | 'no-key';

export interface VerifiedTreeHead {
  treeSize: number;
  rootHash: Uint8Array;
  /** ms since epoch; from the STH, or from the CT signature body of a checkpoint. */
  timestamp?: number;
  signature: { status: SignatureStatus; error?: string };
  /** The exact text the log served (JSON or checkpoint note). */
  raw: string;
  /** Checkpoint origin line (tiled logs). */
  origin?: string;
  fetchedAt: number;
}

/** Issuer chain elements: full DER (RFC 6962) or a SHA-256 fingerprint to resolve later (tiled). */
export type ChainRef = { der: Uint8Array } | { fingerprint: Uint8Array };

export interface LogEntry {
  index: number;
  leaf: MerkleTreeLeaf;
  leafHash: Uint8Array;
  entryType: EntryType;
  /** x509: the leaf certificate DER. precert: the poison-free TBSCertificate. */
  cert: Uint8Array;
  /** precert entries: the full precertificate DER, when the log provides it. */
  precert?: Uint8Array;
  issuerKeyHash?: Uint8Array;
  chain: ChainRef[];
}

export interface InclusionProof {
  leafIndex: number;
  treeSize: number;
  auditPath: Uint8Array[];
  /** The leaf the log returned alongside the proof (get-entry-and-proof / tiles). */
  leaf?: Uint8Array;
  leafHash?: Uint8Array;
}

export interface ConsistencyProof {
  first: number;
  second: number;
  proof: Uint8Array[];
}

export type InclusionTarget = { index: number } | { leafHash: Uint8Array };

export interface GetEntriesOptions {
  /** Known tree size, so truncated batches at the tree end are not mistaken for a server cap. */
  treeSize?: number;
  signal?: AbortSignal;
}

/** Unified surface over RFC 6962 and static-ct-api logs. */
export interface LogClient {
  readonly log: LogDescriptor;
  readonly kind: LogKind;
  /** Natural page size: 256 (a tile) for tiled logs, the observed batch cap for RFC 6962 logs. */
  readonly pageSize: number;
  getTreeHead(signal?: AbortSignal): Promise<VerifiedTreeHead>;
  /** May return fewer entries than requested; callers loop. */
  getEntries(start: number, count: number, opts?: GetEntriesOptions): Promise<LogEntry[]>;
  getInclusionProof(target: InclusionTarget, treeSize: number, signal?: AbortSignal): Promise<InclusionProof>;
  getConsistency(first: number, second: number, signal?: AbortSignal): Promise<ConsistencyProof>;
  getRoots(signal?: AbortSignal): Promise<Uint8Array[]>;
  addChain(chain: Uint8Array[], precert: boolean, signal?: AbortSignal): Promise<Sct>;
  /** Tiled logs only: fetch an issuer certificate by SHA-256 fingerprint. */
  resolveIssuer?(fingerprint: Uint8Array, signal?: AbortSignal): Promise<Uint8Array>;
  /** Whether this client can look up an entry by leaf hash (RFC 6962 yes, tiled no). */
  readonly supportsHashLookup: boolean;
}

export const STATE_ORDER: LogState[] = ['usable', 'qualified', 'readonly', 'pending', 'retired', 'rejected', 'unknown'];

/** States in which a log is worth showing by default (accepting or serving entries). */
export const ACTIVE_STATES: ReadonlySet<LogState> = new Set(['usable', 'qualified', 'readonly']);

/** Whether any program still considers the log active. */
export const isActiveLog = (l: LogDescriptor): boolean => l.custom === true || l.programs.some((p) => ACTIVE_STATES.has(p.state));

/** Whether the log's temporal interval accepts a certificate with this notAfter. */
export function acceptsExpiry(l: LogDescriptor, notAfter: Date): boolean {
  if (!l.temporalInterval) return true;
  const t = notAfter.getTime();
  return t >= Date.parse(l.temporalInterval.start) && t < Date.parse(l.temporalInterval.end);
}
